import { ComplementStatus, Report } from "@/types/report";
import { isArrayWithData } from "@/utils/validations";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { Button } from "@aidergroup/ui-v2";
import React from "react";
import Attachment from "./attachment";
import SectionTitle from "@/components/ui/section-title";
import { i18n } from "@/utils/i18n";

interface Props {
    attachments: Report["attachments"];
    complementStatus: ComplementStatus;
    onAcknowledgeComplement?: () => void;
    acknowledgingComplement?: boolean;
    onToggleAttachmentCompletion?: (status: ComplementStatus) => void;
    togglingAttachmentCompletion?: boolean;
}

const Attachments: React.FC<Props> = ({
    attachments,
    complementStatus,
    onAcknowledgeComplement,
    acknowledgingComplement,
    onToggleAttachmentCompletion,
    togglingAttachmentCompletion,
}) => {
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [requestOpen, setRequestOpen] = React.useState(false);

    const handleConfirm = () => {
        setConfirmOpen(false);
        onAcknowledgeComplement?.();
    };

    const handleRequestConfirm = () => {
        setRequestOpen(false);
        onToggleAttachmentCompletion?.(complementStatus);
    };

    const regularAttachments = attachments?.filter(
        i => i.attachmentType !== "SignedReport" && i.attachmentType !== "AttachmentComplement",
    );
    const complementAttachments = attachments?.filter(
        i => i.attachmentType === "AttachmentComplement",
    );

    return (
        <Box display="flex">
            <Box flex={1}>
                <SectionTitle title={i18n.t("annualReport")} />
                {isArrayWithData(regularAttachments) ? (
                    <Box paddingX={1} marginTop={1} display="flex" gap={2}>
                        <FolderCopyIcon sx={{ color: "gray" }} />
                        <Box display="flex" flexDirection="column" gap={1}>
                            {regularAttachments.map((i, index) => (
                                <Attachment {...i} key={index} />
                            ))}
                        </Box>
                    </Box>
                ) : null}
            </Box>

            <Box flex={1}>
                <SectionTitle title={i18n.t("complementAttachments")} />
                <Box paddingX={1} marginTop={1} display="flex" gap={2}>
                    <PostAddIcon sx={{ color: isArrayWithData(complementAttachments) ? "#e67e22" : "gray" }} />
                    <Box display="flex" flexDirection="column" gap={1}>
                        {isArrayWithData(complementAttachments) ? (
                            <>
                                {complementAttachments.map((i, index) => (
                                    <Attachment {...i} key={index} />
                                ))}
                                {complementStatus === ComplementStatus.Received && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.5,
                                            border: "1px solid #f5e0b0",
                                            backgroundColor: "#fef9ef",
                                            borderRadius: 2,
                                            p: 2,
                                        }}
                                    >
                                        <Box display="flex" alignItems="flex-start" gap={1}>
                                            <CheckCircleOutlineIcon sx={{ color: "#e67e22", fontSize: 20, mt: 0.2 }} />
                                            <Typography variant="caption" sx={{ color: "#6b4c00" }}>
                                                {i18n.t("complementReceivedMessage")}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            onClick={() => setConfirmOpen(true)}
                                            disabled={acknowledgingComplement}
                                            sx={{ alignSelf: "flex-start" }}
                                        >
                                            <CheckCircleOutlineIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                            {i18n.t("acknowledgeComplement")}
                                        </Button>
                                    </Box>
                                )}
                                {complementStatus === ComplementStatus.Acknowledged && (
                                    <Chip
                                        icon={<CheckCircleOutlineIcon />}
                                        label={i18n.t("complementAcknowledged")}
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                        sx={{ mt: 2 }}
                                    />
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t("noComplementAttachments")}
                            </Typography>
                        )}
                        <Button
                            variant={complementStatus === ComplementStatus.Requested ? "outlined" : "contained"}
                            disabled={togglingAttachmentCompletion}
                            onClick={() => {
                                if (complementStatus === ComplementStatus.Requested) {
                                    onToggleAttachmentCompletion?.(complementStatus);
                                } else {
                                    setRequestOpen(true);
                                }
                            }}
                            sx={{ alignSelf: "flex-start", mt: 2 }}
                        >
                            {i18n.t(
                                complementStatus === ComplementStatus.Requested
                                    ? "button.cancelAttachmentCompletion"
                                    : "button.requestAttachmentCompletion",
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleOutlineIcon color="success" />
                    {i18n.t("acknowledgeComplementTitle")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t("acknowledgeComplementDescription")}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
                        {i18n.t("button.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={handleConfirm}
                        disabled={acknowledgingComplement}
                    >
                        {i18n.t("acknowledgeComplementConfirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PostAddIcon color="primary" />
                    {i18n.t("requestComplementTitle")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t("requestComplementDescription")}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="outlined" onClick={() => setRequestOpen(false)}>
                        {i18n.t("button.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRequestConfirm}
                        disabled={togglingAttachmentCompletion}
                    >
                        {i18n.t("requestComplementConfirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Attachments;
